import type { WebSocketMessage } from '../../interfaces/WebSocketMessage';
import { resolveAiEventFileRef } from './resolveAiEventFileRef';

const MAX_STRING_LENGTH = 2_000;
const MAX_ARRAY_LENGTH = 50;
const MAX_OBJECT_DEPTH = 10;

const SECRET_KEY_PATTERN = /password|token|secret|authorization/i;

function sanitizeValue(value: unknown, depth: number): unknown {
  if (depth > MAX_OBJECT_DEPTH) {
    return '[MaxDepth]';
  }

  if (value == null || typeof value !== 'object') {
    if (typeof value === 'string' && value.length > MAX_STRING_LENGTH) {
      return `${value.slice(0, MAX_STRING_LENGTH)}…`;
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_LENGTH).map((entry) => sanitizeValue(entry, depth + 1));
  }

  const sanitized: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_KEY_PATTERN.test(key)) {
      continue;
    }
    sanitized[key] = sanitizeValue(entry, depth + 1);
  }
  return sanitized;
}

/** JSON-safe copy of incoming WS event for notification debug metadata. */
export function captureWsSourcePayload(event: WebSocketMessage): Record<string, unknown> {
  return sanitizeValue(event, 0) as Record<string, unknown>;
}

type WsNotificationLogMeta = {
  action?: 'create' | 'skip';
  reason?: string;
};

function readAiEventData(event: WebSocketMessage): Record<string, unknown> {
  return event.data != null && typeof event.data === 'object'
    ? (event.data as Record<string, unknown>)
    : {};
}

/** Dev-only structured log for notification-relevant WS events. */
export function logWsNotificationEvent(
  event: WebSocketMessage,
  meta: WsNotificationLogMeta = {},
): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const data = readAiEventData(event);

  console.debug('[notifications:ws]', {
    ...meta,
    wsType: event.type,
    eventId: event.id,
    event_type: data.event_type,
    status: data.status,
    file_id: data.file_id,
    file_ids: data.file_ids,
    fileId: data.fileId,
    fileIds: data.fileIds,
    resolvedFileRef: resolveAiEventFileRef(data),
    error_message: data.error_message,
    payload: captureWsSourcePayload(event),
  });
}
